import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle } from 'lucide-react';
import {
  FriendAccessLevel,
  ACCESS_LEVEL_LABELS,
  ACCESS_LEVEL_DESCRIPTIONS,
} from '../types/friends';
import { motion, AnimatePresence } from 'framer-motion';

interface AccessLevelChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentAccessLevel: FriendAccessLevel;
  friendName: string;
  onUpdate: (accessLevel: FriendAccessLevel) => Promise<void>;
}

const AccessLevelChangeModal = ({
  isOpen,
  onClose,
  currentAccessLevel,
  friendName,
  onUpdate,
}: AccessLevelChangeModalProps) => {
  const [newAccessLevel, setNewAccessLevel] = useState<FriendAccessLevel>(currentAccessLevel);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const isDowngrading = currentAccessLevel === 'full-access' && newAccessLevel === 'restricted-access';

  const handleUpdate = async () => {
    if (newAccessLevel === currentAccessLevel) {
      onClose();
      return;
    }

    if (isDowngrading && !showConfirm) {
      setShowConfirm(true);
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      await onUpdate(newAccessLevel);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update access level');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">Change Access Level</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Changing access level for <span className="font-semibold">{friendName}</span>
                  </p>
                  <p className="text-sm font-medium text-gray-700 mb-3">Current: {ACCESS_LEVEL_LABELS[currentAccessLevel]}</p>
                </div>

                {isDowngrading && showConfirm && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-yellow-800 mb-1">Warning</p>
                        <p className="text-sm text-yellow-700">
                          Changing to Restricted access means {friendName} will only see cruises you
                          explicitly share with them. They will lose access to cruises that aren't
                          currently shared.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    New Access Level
                  </label>
                  <div className="space-y-3">
                    {(['full-access', 'restricted-access'] as FriendAccessLevel[]).map((level) => (
                      <label
                        key={level}
                        className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          newAccessLevel === level
                            ? 'border-secondary-500 bg-secondary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${level === currentAccessLevel ? 'opacity-50' : ''}`}
                      >
                        <input
                          type="radio"
                          name="newAccessLevel"
                          value={level}
                          checked={newAccessLevel === level}
                          onChange={(e) => {
                            setNewAccessLevel(e.target.value as FriendAccessLevel);
                            setShowConfirm(false);
                          }}
                          className="mt-1 mr-3"
                          disabled={isLoading || level === currentAccessLevel}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900">
                              {ACCESS_LEVEL_LABELS[level]}
                            </span>
                            {level === currentAccessLevel && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {ACCESS_LEVEL_DESCRIPTIONS[level]}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdate}
                    className="flex-1 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    disabled={isLoading || newAccessLevel === currentAccessLevel}
                  >
                    {isLoading
                      ? 'Updating...'
                      : showConfirm
                      ? 'Confirm Change'
                      : 'Update Access Level'}
                  </button>
                </div>
              </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default AccessLevelChangeModal;

